// Article "1,60 m et permis A2 : petite taille, grandes solutions"
import { Ruler, Bike, Compass, Sparkles, ShieldCheck, Target, Lightbulb } from 'lucide-react';

export const petitGabaritArticle = {
  slug: 'permis-a2-petit-gabarit-160-solutions',
  weekVolume: 31,
  weekYear: 2026,
  monthLabel: 'Août 2026',
  category: 'Pédagogie & plateau',
  shortTitle: '1,60 m et permis A2',
  title: '1,60 m et permis A2 : petite taille, grandes solutions',
  kicker: 'Formation initiale · Plateau moto A1/A2',
  subtitle: "Poser les deux pieds à plat n'est pas une condition pour savoir conduire une moto. Pour un élève de petit gabarit, la véritable compétence consiste à apprendre à maîtriser son équilibre et à choisir son appui. Et cela commence dès la première leçon de plateau.",
  heroImage: 'https://images.unsplash.com/photo-1591637333184-19aa84b3e01f?w=2400&q=80&auto=format&fit=crop',
  heroImageAlt: "Moto naked type A2 sur route, silhouette adaptée aux petits gabarits",
  topRightKicker: { line1: 'Dossier CODE 2 ROUES', line2: 'Petit gabarit · A2' },
  readTimeMin: 6,
  seoDescription: "Petit gabarit et permis A2 : comment maîtriser l'arrêt en moto quand on mesure 1,60 m ? Techniques, choix moto (CFMoto 450NK, Honda CB500) et pédagogie pour les premiers cours plateau.",
  seoKeywords: 'permis A2 petit gabarit, femme moto 1m60, arrêt pied moto, hauteur selle A2, CFMoto 450NK, Honda CB500, plateau moto',
  about: "Pédagogie moto, petit gabarit, premier cours plateau, choix moto A2",
  source: { label: 'CODE 2 ROUES · Rédaction', refs: 'Article rédigé par Bruno · Équipe Code 2 Roues · Analyse pédagogique terrain.' },
  chapo: {
    kicker: 'Pourquoi cet article',
    body: "Une élève d'environ <strong>1,60 m</strong> qui débute le permis moto A2 rencontre fréquemment la même difficulté : <em>la stabilisation de la moto à l'arrêt</em>. Ce n'est pourtant pas tant sa taille qui pose problème que <strong>la stratégie</strong> qu'elle utilise pour s'arrêter.",
    sideStat: {
      kicker: 'Idée reçue',
      big: '❌',
      body: "\u00ab\u00a0Il faut poser les deux pieds à plat pour conduire.\u00a0\u00bb <strong>FAUX</strong>. Un pied bien posé vaut mieux que deux pieds en équilibre sur la pointe.",
      accent: '#ef4444',
    },
  },
  pullQuote: {
    text: "Ce n'est pas le pied qui doit sauver une moto déséquilibrée. C'est la moto correctement stabilisée qui permet ensuite de poser tranquillement le pied.",
    source: 'CODE 2 ROUES — Conseil pédagogique',
  },
  sections: [
    {
      part: 'Partie 1 — L\'idée à déconstruire',
      title: 'Deux pieds à plat ? Pas forcément.',
      paragraphs: [
        "Lorsqu'un élève monte sur une moto et constate qu'il ne touche le sol que de la pointe des pieds, sa réaction naturelle est souvent : <em>« Je suis trop petite pour cette moto. »</em> Pas nécessairement.",
        "La taille du pilote n'est qu'une partie de l'équation. La <strong>longueur d'entrejambe</strong>, la <strong>largeur de la selle</strong>, sa forme, la <strong>répartition des masses</strong> et le <strong>poids de la moto</strong> jouent un rôle considérable. Deux personnes mesurant exactement 1,60 m peuvent avoir des sensations très différentes sur la même machine.",
        "Il n'est pas indispensable de poser simultanément les deux pieds à plat pour stabiliser correctement une moto à l'arrêt.",
      ],
      cards: [
        { icon: Ruler, accent: '#f97316', kicker: 'Morphologie', title: "Entrejambe > taille totale", body: "Deux personnes d'1,60 m peuvent avoir des jambes de longueur très différente. C'est <strong>l'entrejambe</strong> qui détermine l'accès au sol, pas la taille debout." },
        { icon: Bike, accent: '#cf2031', kicker: 'Ergonomie', title: 'Largeur & forme de selle', body: "Une selle <strong>affinée</strong> permet aux jambes de descendre verticalement. Une selle large éloigne artificiellement les pieds du sol." },
      ],
      cardsCols: 2,
    },
    {
      part: 'Partie 2 — Le vrai enjeu',
      title: 'Le véritable ennemi : les quelques degrés de trop',
      paragraphs: [
        "À l'arrêt, une moto <strong>parfaitement verticale</strong> demande relativement peu d'effort pour être maintenue en équilibre. Mais laissez-la prendre quelques degrés d'inclinaison et tout change : le pilote doit désormais retenir une partie de son poids.",
        "Sur une moto de 160, 180 ou 190 kg, cette différence devient immédiatement perceptible, particulièrement pour un petit gabarit. C'est donc <strong>avant de poser le pied</strong> que se joue une grande partie de la réussite.",
      ],
      highlight: {
        kicker: '🔑 La séquence à construire',
        title: 'REGARD → MOTO DROITE → FREINAGE PROGRESSIF → ARRÊT → APPUI',
        body: "Ce n'est pas le pied qui doit sauver une moto déséquilibrée. C'est la moto correctement stabilisée qui permet ensuite de poser tranquillement le pied.",
        accent: '#22c55e',
      },
    },
    {
      part: 'Partie 3 — La technique',
      title: 'Le bassin : quelques centimètres qui changent tout',
      paragraphs: [
        "C'est probablement la technique la plus intéressante à faire découvrir dès la première séance. Au lieu de rester parfaitement centré sur la selle et d'essayer désespérément de descendre les deux jambes, le pilote peut effectuer un <strong>léger décalage du bassin</strong> vers le côté choisi.",
        "Pas question de descendre complètement de la selle. <strong>Quelques centimètres peuvent suffire.</strong> La jambe gagne alors de l'allonge et permet d'obtenir un appui beaucoup plus solide.",
      ],
      cards: [
        { icon: Target, accent: '#f97316', kicker: 'Étape 1', title: 'Position centrée', body: 'Le pilote assis normalement, moto verticale, en approche d\'arrêt.' },
        { icon: Compass, accent: '#eab308', kicker: 'Étape 2', title: 'Bassin décalé', body: 'Léger transfert du bassin vers le côté choisi (2-5 cm suffisent).' },
        { icon: ShieldCheck, accent: '#22c55e', kicker: 'Étape 3', title: 'Appui solide', body: 'La jambe descend verticalement, le pied trouve un appui franc.' },
      ],
      cardsCols: 3,
    },
    {
      part: 'Partie 4 — Pédagogie',
      title: 'Essayez sans même démarrer',
      paragraphs: [
        "Pour une première découverte, <strong>le moteur peut rester coupé</strong>. L'enseignant sécurise la moto pendant que l'élève expérimente : position centrée → léger décalage du bassin → descente de la jambe → appui du pied → retour en position normale.",
        "Puis on recommence. Sans vitesse, sans parcours, sans pression. L'objectif n'est pas encore de conduire. L'objectif est de faire comprendre à l'élève : <em>« Je sais comment stabiliser cette moto. »</em>",
      ],
    },
    {
      part: 'Partie 5 — Adaptation',
      title: 'Pied gauche ou pied droit ?',
      paragraphs: [
        "Pour les premiers exercices, <strong>l'appui gauche</strong> constitue souvent une situation pratique : pied gauche au sol, pied droit disponible sur son repose-pied. Mais attention à ne pas transformer cette technique en règle absolue.",
        "La route n'est pas un plateau parfaitement horizontal. Un arrêt en dévers peut changer la situation. Si le sol descend fortement à gauche, vouloir absolument utiliser ce pied peut devenir particulièrement inconfortable.",
        "Un motard doit progressivement apprendre à : <strong>observer le terrain → choisir son côté → préparer son arrêt</strong>. Le but de la formation n'est pas de fabriquer un automatisme rigide. C'est de développer la <em>capacité d'adaptation</em>.",
      ],
    },
    {
      part: 'Partie 6 — Choix de la moto',
      title: 'CFMoto 450NK, Honda CB500 : laquelle rassurera davantage ?',
      paragraphs: [
        "Pour une école de conduite accueillant régulièrement de petits gabarits, le choix du parc moto mérite une vraie réflexion.",
        "La <strong>CFMoto 450NK</strong> présente un argument intéressant : <strong>165 kg tous pleins faits</strong> et une selle annoncée à <strong>795 mm</strong>, affinée pour faciliter l'accès au sol.",
        "Une <strong>Honda CB500</strong> peut également convenir à de nombreux élèves, mais l'ergonomie et surtout le <strong>poids ressenti à très basse vitesse</strong> devront être évalués avec chaque personne. Le meilleur juge restera toujours… <em>l'élève assis sur la moto</em>.",
      ],
      highlight: {
        kicker: '💡 Attention',
        title: 'La fiche technique ne raconte pas toute l\'histoire',
        body: "Une selle annoncée à 795 mm n'offre pas la même accessibilité sur deux motos différentes. Une selle étroite permet aux jambes de descendre plus verticalement ; une selle large augmente la distance réelle jusqu'au sol.",
        accent: '#eab308',
      },
    },
    {
      part: 'Le mot de la fin',
      title: 'La confiance commence à 0 km/h',
      paragraphs: [
        "La première victoire d'un élève de petit gabarit n'est peut-être pas son premier slalom réussi. Ce n'est pas non plus son premier demi-tour.",
        "C'est parfois beaucoup plus simple : <strong>s'arrêter, sentir la moto se stabiliser, poser tranquillement son pied</strong>, et comprendre qu'elle ne va pas tomber. À cet instant, la moto qui semblait trop grande quelques minutes auparavant commence déjà à devenir <em>sa moto</em>.",
      ],
      cards: [
        { icon: Sparkles, accent: '#cf2031', kicker: 'Le conseil CODE 2 ROUES', title: '4 questions à se poser', body: "Est-ce que je maîtrise mon arrêt ?<br>Est-ce que je garde la moto droite ?<br>Est-ce que je sais choisir mon pied d'appui ?<br>Est-ce que je peux répéter sans appréhension ?" },
        { icon: Lightbulb, accent: '#22c55e', kicker: 'Le paradoxe positif', title: 'Petit gabarit = école exigeante', body: "Un pilote qui touche facilement le sol peut masquer certaines approximations. Le petit gabarit apprend obligatoirement que <strong>l'équilibre se prépare avec le regard, l'allure et l'anticipation</strong> — pas avec les pieds. Cette contrainte produit souvent une conduite particulièrement propre." },
      ],
      cardsCols: 2,
    },
  ],
  relatedRessources: [
    { label: 'Infographie 1er cours plateau — Petit gabarit', href: '/ressources-pedagogiques', desc: 'Fiche visuelle A3 téléchargeable' },
    { label: 'Livret plateau A2 — 7 chapitres', href: '/livret-plateau', desc: 'Version interactive premium' },
  ],
};
